import React, { useEffect, useState } from 'react';
import Bluebird from 'bluebird';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, generatePath } from 'react-router-dom';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import { Add, Delete, Edit, Group as GroupIcon } from '@mui/icons-material';
import { confirmAlert } from 'react-confirm-alert';
import * as groupsActions from '../actions/groups';
import { Roles } from '../models/roles';
import { currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite } from '../services/auth';
import useActiveSite from '../hooks/useActiveSite';
import snackbarHelper from '../helpers/snackbarHelper';
import BaseMUIDataTable from '../components/BaseMUIDataTable/BaseMUIDataTable';
import ConfirmationDialog from '../components/ConfirmationDialog';
import routes from '../routes';
import GroupMembersView from './GroupMembersView';

const EMPTY_FORM = { name: '', description: '' };

const GroupsListView = ({ subTab }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const activeSite = useActiveSite();

  const groups = useSelector(state => state.entities.groups);
  const siteGroupIds = groups.bySiteId[activeSite.id] || [];
  const groupsList = siteGroupIds.map(id => groups.byId[id]).filter(Boolean);

  const isSiteDirector = currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
    Roles.Admin,
    Roles.NationalSiteDirector,
    Roles.SiteDirector,
  ]);

  // ── Group form dialog state ──────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [formSubmitting, setFormSubmitting] = useState(false);

  // ── Inline edit state ────────────────────────────────────────────────────
  const [inlineEditId, setInlineEditId] = useState(null);
  const [inlineEditField, setInlineEditField] = useState(null);
  const [inlineEditValue, setInlineEditValue] = useState('');

  useEffect(() => {
    dispatch(groupsActions.fetchSiteGroups(activeSite.id));
  }, [activeSite.id]);

  // ── Form helpers ──────────────────────────────────────────────────────────
  const openCreateForm = () => {
    setFormValues(EMPTY_FORM);
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormValues(EMPTY_FORM);
  };

  const handleFormChange = e => setFormValues(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleFormSubmit = () => {
    if (!formValues.name.trim()) {
      snackbarHelper.error('Group name is required.');
      return;
    }

    setFormSubmitting(true);

    Bluebird.try(() => dispatch(groupsActions.createGroup(activeSite.id, formValues)))
      .then(() => {
        snackbarHelper.success('Group created.');
        closeForm();
      })
      .catch(err => snackbarHelper.error(err.message))
      .finally(() => setFormSubmitting(false));
  };

  const handleDeleteGroup = group => {
    if (group.is_system) {
      return;
    }

    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmationDialog
          title="Delete Group"
          message={`Delete group "${group.name}"? This cannot be undone.`}
          buttons={[
            { label: 'Cancel' },
            {
              label: 'Delete',
              color: 'secondary',
              onClick: () => {
                Bluebird.try(() => dispatch(groupsActions.deleteGroup(activeSite.id, group.id)))
                  .then(() => snackbarHelper.success('Group deleted.'))
                  .catch(err => snackbarHelper.error(err.message));
              },
            },
          ]}
          closeDialog={onClose}
        />
      ),
    });
  };

  // ── Inline edit helpers ───────────────────────────────────────────────────
  const startInlineEdit = (group, field) => {
    if (group.is_system) {
      return;
    }

    setInlineEditId(group.id);
    setInlineEditField(field);
    setInlineEditValue(group[field] || '');
  };

  const cancelInlineEdit = () => {
    setInlineEditId(null);
    setInlineEditField(null);
    setInlineEditValue('');
  };

  const saveInlineEdit = (group) => {
    if (inlineEditField === 'name' && !inlineEditValue.trim()) {
      snackbarHelper.error('Group name is required.');
      cancelInlineEdit();
      return;
    }

    const currentValue = group[inlineEditField] || '';
    if (inlineEditValue === currentValue) {
      cancelInlineEdit();
      return;
    }

    Bluebird.try(() =>
      dispatch(groupsActions.updateGroup(activeSite.id, group.id, { [inlineEditField]: inlineEditValue }))
    )
      .then(() => snackbarHelper.success('Group updated.'))
      .catch(err => snackbarHelper.error(err.message))
      .finally(() => cancelInlineEdit());
  };

  const renderInlineCell = (group, field) => {
    const isEditing = inlineEditId === group.id && inlineEditField === field;

    if (isEditing) {
      return (
        <TextField
          value={inlineEditValue}
          onChange={e => setInlineEditValue(e.target.value)}
          onBlur={() => saveInlineEdit(group)}
          onKeyDown={e => {
            if (e.key === 'Enter') saveInlineEdit(group);
            if (e.key === 'Escape') cancelInlineEdit();
          }}
          size="small"
          variant="outlined"
          autoFocus
          style={{ minWidth: 180 }}
        />
      );
    }

    return (
      <Box display="flex" alignItems="center" gap="4px">
        <span>{group[field] || '—'}</span>
        {isSiteDirector && !group.is_system && (
          <Tooltip title={`Edit ${field}`}>
            <IconButton size="small" onClick={() => startInlineEdit(group, field)}>
              <Edit fontSize="inherit" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // ── Table columns ─────────────────────────────────────────────────────────
  const columns = [
    {
      name: 'id',
      label: 'ID',
      options: { display: false, filter: false },
    },
    {
      name: 'name',
      label: 'Name',
      options: {
        filter: false,
        customBodyRenderLite: dataIndex => {
          const group = groupsList[dataIndex];
          if (!group) return null;
          return renderInlineCell(group, 'name');
        },
      },
    },
    {
      name: 'description',
      label: 'Description',
      options: {
        filter: false,
        customBodyRenderLite: dataIndex => {
          const group = groupsList[dataIndex];
          if (!group) return null;
          return renderInlineCell(group, 'description');
        },
      },
    },
    {
      name: 'users',
      label: 'Members',
      options: {
        filter: false,
        sort: false,
        customBodyRender: value => (value || []).length,
        customBodyRenderCSV: value => (value || []).length,
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRenderLite: dataIndex => {
          const group = groupsList[dataIndex];
          if (!group) return null;

          return (
            <Box display="flex" gap="4px">
              <Tooltip title="Manage members">
                <IconButton
                  size="small"
                  onClick={() =>
                    history.push(
                      generatePath(routes.allUsers, { tab: 'groups', subTab: group.id })
                    )
                  }
                >
                  <GroupIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {isSiteDirector && !group.is_system && (
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleDeleteGroup(group)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        },
      },
    },
  ];

  // ── If a subTab is set, show GroupMembersView ─────────────────────────────
  if (subTab) {
    return <GroupMembersView subTab={subTab} />;
  }

  return (
    <>
      <Grid container>
        <Grid item xs={12}>
          <BaseMUIDataTable
            tableId="#groups/site-groups"
            title="Groups"
            data={groupsList}
            columns={columns}
            options={{
              selectableRows: 'none',
              customToolbar: isSiteDirector
                ? () => (
                    <Tooltip title="Create group">
                      <Button
                        size="small"
                        startIcon={<Add />}
                        variant="outlined"
                        color="primary"
                        onClick={openCreateForm}
                      >
                        New Group
                      </Button>
                    </Tooltip>
                  )
                : undefined,
            }}
          />
        </Grid>
      </Grid>

      {/* ── Create dialog ──────────────────────────────────────────────────── */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>New Group</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} pt={1}>
            <TextField
              label="Name"
              name="name"
              value={formValues.name}
              onChange={handleFormChange}
              variant="outlined"
              fullWidth
              required
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label="Description (optional)"
              name="description"
              value={formValues.description}
              onChange={handleFormChange}
              variant="outlined"
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForm} disabled={formSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleFormSubmit} color="primary" variant="contained" disabled={formSubmitting}>
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GroupsListView;
